import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Download, FileText, Image as ImageIcon, Music, Video } from "lucide-react";

import { listQuery } from "@/lib/queries";
import { EmptyState, PageHero, Section } from "@/components/site/bits";

export const Route = createFileRoute("/materiais")({
  head: () => ({
    meta: [
      { title: "Materiais para download | Professor Rurdiney 40123" },
      {
        name: "description",
        content:
          "Baixe os materiais da campanha do Professor Rurdiney 40123: santinhos, adesivos, cards para redes sociais, documentos e mais.",
      },
      { property: "og:title", content: "Materiais para download | Professor Rurdiney 40123" },
      {
        property: "og:description",
        content: "Materiais oficiais da campanha disponíveis para download gratuito.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(listQuery("materials")),
  component: MateriaisPage,
});

const ICON: Record<string, typeof FileText> = {
  documento: FileText,
  imagem: ImageIcon,
  video: Video,
  audio: Music,
};

function MateriaisPage() {
  const { data } = useSuspenseQuery(listQuery("materials"));
  const items = (data ?? []) as any[];

  return (
    <>
      <PageHero
        eyebrow="Download"
        title="Materiais de campanha"
        description="Baixe e compartilhe os materiais oficiais do Professor Rurdiney."
      />
      <Section>
        {items.length === 0 ? (
          <EmptyState message="Nenhum material disponível para download no momento." />
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((m) => {
              const Icon = ICON[m.kind] ?? FileText;
              return (
                <article
                  key={m.id}
                  className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
                >
                  {m.cover_url ? (
                    <img
                      src={m.cover_url}
                      alt={m.title}
                      loading="lazy"
                      className="h-44 w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-44 w-full items-center justify-center bg-secondary">
                      <Icon className="size-10 text-primary" aria-hidden="true" />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-5">
                    <h2 className="font-display text-xl text-foreground">{m.title}</h2>
                    {m.description ? (
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                        {m.description}
                      </p>
                    ) : (
                      <div className="flex-1" />
                    )}
                    <div className="mt-4 flex items-center justify-between gap-3">
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">
                        {m.kind}
                        {m.file_size ? ` • ${m.file_size}` : ""}
                      </span>
                      <a
                        href={m.file_url}
                        target="_blank"
                        rel="noreferrer"
                        download
                        className="inline-flex items-center gap-2 rounded-xl bg-accent px-4 py-2 text-sm font-bold uppercase tracking-wide text-accent-foreground transition-transform hover:scale-105"
                      >
                        <Download className="size-4" /> Baixar
                      </a>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </Section>
    </>
  );
}
